import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Crown, Clock, Check, Printer, X, MapPin, User, CalendarDays, AlertTriangle, Sun, Cloud, CloudRain, CloudSnow, CloudLightning, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Weather code to icon component mapping
const getWeatherIcon = (code, size = 'sm') => {
  const sizeClass = { sm: 'h-3 w-3', md: 'h-4 w-4' }[size];
  
  if (code === 0 || code === 1) return <Sun className={`${sizeClass} text-yellow-500`} />;
  if (code <= 3) return <Sun className={`${sizeClass} text-yellow-400`} />;
  if (code <= 49) return <Cloud className={`${sizeClass} text-gray-400`} />;
  if (code <= 69) return <CloudRain className={`${sizeClass} text-blue-500`} />;
  if (code <= 79) return <CloudSnow className={`${sizeClass} text-blue-200`} />;
  if (code <= 99) return <CloudLightning className={`${sizeClass} text-purple-500`} />;
  return <Cloud className={`${sizeClass} text-gray-400`} />;
};

const getWeatherEmoji = (code) => {
  if (code === 0 || code === 1) return '☀️';
  if (code <= 3) return '⛅';
  if (code <= 49) return '🌫️';
  if (code <= 69) return '🌧️';
  if (code <= 79) return '❄️';
  if (code <= 99) return '⛈️';
  return '☁️';
};

const MonthlyCalendar = ({ reminders = [], plannedActions = [], queenBatches = [], treatmentPlans = [], yards = [], onActionUpdate, onReminderUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayPopup, setShowDayPopup] = useState(false);
  const [forecast, setForecast] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [rescheduleItem, setRescheduleItem] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [selectedApiary, setSelectedApiary] = useState('all');
  const [selectedApiaryName, setSelectedApiaryName] = useState('All Apiaries');
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const navigate = useNavigate();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Fetch weather based on selected apiary location
  const fetchWeather = useCallback(async (lat, lon) => {
    try {
      setWeatherLoading(true);
      
      let latitude = lat;
      let longitude = lon;
      
      // If no coords provided, try to get from selected apiary or current location
      if (!latitude || !longitude) {
        if (selectedApiary && selectedApiary !== 'all') {
          const yard = yards.find(y => y.id === selectedApiary || y.name === selectedApiary);
          if (yard) {
            latitude = yard.latitude || yard.gps_lat;
            longitude = yard.longitude || yard.gps_lng;
          }
        }
        
        // Fallback to current location
        if (!latitude || !longitude || latitude === 0 || longitude === 0) {
          const position = await new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
              reject(new Error('No geolocation'));
              return;
            }
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          }).catch(() => ({ coords: { latitude: -33.87, longitude: 151.21 } }));
          
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
        }
      }
      
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max` +
        `&timezone=auto&forecast_days=16`
      );
      
      if (!response.ok) throw new Error('Weather fetch failed');
      
      const data = await response.json();
      
      // Build forecast map by date
      const forecastMap = {};
      data.daily.time.forEach((date, idx) => {
        forecastMap[date] = {
          code: data.daily.weather_code[idx],
          tempMax: Math.round(data.daily.temperature_2m_max[idx]),
          tempMin: Math.round(data.daily.temperature_2m_min[idx]),
          precipProb: data.daily.precipitation_probability_max[idx] || 0
        };
      });
      
      setForecast(forecastMap);
    } catch (err) {
    } finally {
      setWeatherLoading(false);
    }
  }, [selectedApiary, yards]);

  // Handle apiary selection change
  const handleApiaryChange = useCallback((value) => {
    setSelectedApiary(value);
    
    if (value === 'all') {
      setSelectedApiaryName('All Apiaries');
      fetchWeather(); // Use current location
    } else {
      const yard = yards.find(y => y.id === value || y.name === value);
      if (yard) {
        const locationParts = yard.location?.split(',').map(s => s.trim()) || [];
        const locationInfo = locationParts.slice(-2).join(', ') || yard.suburb || '';
        setSelectedApiaryName(`${yard.name}${locationInfo ? ` - ${locationInfo}` : ''}`);
        
        const lat = yard.latitude || yard.gps_lat;
        const lon = yard.longitude || yard.gps_lng;
        
        if (lat && lon && lat !== 0 && lon !== 0) {
          fetchWeather(lat, lon);
        } else {
          toast.error(`No location set for ${yard.name}`);
        }
      }
    }
  }, [yards, fetchWeather]);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay, year, month };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDay(today.getDate());
  };

  const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentDate);

  const handlePrint = () => {
    window.print();
  };

  // Get weather for a specific date
  const getWeatherForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return forecast[dateStr] || null;
  };

  // Check if it's a bad weather day
  const isBadWeather = (day) => {
    const weather = getWeatherForDate(day);
    if (!weather) return false;
    return weather.code >= 51 || weather.precipProb > 60;
  };

  // Get queen rearing events for the month
  const getQueenEventsForDate = (day) => {
    const date = new Date(year, month, day);
    const events = [];
    
    queenBatches.filter(b => !['complete', 'archived', 'failed'].includes(b.status) && b.graft_date).forEach(batch => {
      const gp = batch.graft_date.split('-');
      const graftDate = new Date(parseInt(gp[0]), parseInt(gp[1]) - 1, parseInt(gp[2]));
      
      const milestones = [
        { days: 0, label: 'Graft day', type: 'graft' },
        { days: 4, label: 'Check cells', type: 'check' },
        { days: 10, label: 'Transfer', type: 'transfer' },
        { days: 16, label: 'Queens emerge', type: 'emerge' },
        { days: 28, label: 'Check laying', type: 'laying' }
      ];
      
      milestones.forEach(m => {
        const eventDate = new Date(graftDate);
        eventDate.setDate(eventDate.getDate() + m.days);
        if (eventDate.toDateString() === date.toDateString()) {
          events.push({
            id: `queen-${batch.id}-${m.type}`,
            label: m.label,
            batchName: batch.batch_name || `Batch ${batch.id.slice(0,6)}`,
            batchId: batch.id,
            type: 'queen'
          });
        }
      });
    });
    
    return events;
  };

  const getItemsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(year, month, day);
    
    // Filter reminders by date (exclude queen_rearing type - they're shown separately)
    let dayReminders = reminders.filter(r => {
      if (!r.date) return false;
      if (r.type === 'queen_rearing') return false; // Exclude queen rearing - shown separately
      const reminderDate = new Date(r.date);
      return reminderDate.toDateString() === date.toDateString();
    });

    // Filter actions by date
    let dayActions = plannedActions.filter(a => {
      if (!a.date) return false;
      let actionDateStr = a.date;
      if (a.date.includes('T')) {
        actionDateStr = a.date.split('T')[0];
      }
      return actionDateStr === dateStr;
    });
    
    // Apply apiary filter if not "all"
    if (selectedApiary && selectedApiary !== 'all') {
      const selectedYard = yards.find(y => y.id === selectedApiary || y.name === selectedApiary);
      const selectedYardName = selectedYard?.name;
      
      if (selectedYardName) {
        dayReminders = dayReminders.filter(r => r.yard === selectedYardName);
        dayActions = dayActions.filter(a => a.yard === selectedYardName);
      }
    }
    
    const queenEvents = getQueenEventsForDate(day);

    // Treatment tasks for this day
    let treatmentEvents = [];
    for (const plan of treatmentPlans) {
      for (const task of (plan.tasks || [])) {
        if (!task.date) continue;
        const taskDateStr = task.date.includes('T') ? task.date.split('T')[0] : task.date;
        if (taskDateStr === dateStr) {
          treatmentEvents.push({
            ...task,
            planName: plan.name || plan.treatment_type_name || plan.treatment_type || 'Treatment',
            planStatus: plan.status,
            hive_ids: plan.hive_ids,
          });
        }
      }
    }

    return { reminders: dayReminders, actions: dayActions, queenEvents, treatmentEvents };
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === month && 
           today.getFullYear() === year;
  };

  const isPast = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(year, month, day);
    return checkDate < today;
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowDayPopup(true);
    setRescheduleItem(null);
  };

  // Reschedule an action to a new date
  const handleReschedule = async () => {
    if (!rescheduleItem || !newDate) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (rescheduleItem.itemType === 'action') {
        await axios.put(`${API}/api/planned-actions/${rescheduleItem.id}`, {
          ...rescheduleItem,
          date: newDate
        }, { headers });
        
        toast.success(`Rescheduled to ${new Date(newDate).toLocaleDateString()}`);
        onActionUpdate?.();
      } else if (rescheduleItem.itemType === 'reminder') {
        // Create a planned action from the reminder with new date
        await axios.post(`${API}/api/hives/task/snooze-reminder`, {
          hiveId: rescheduleItem.hiveId,
          action: rescheduleItem.action,
          newDate: newDate,
          assignedBeekeeper: rescheduleItem.assignedBeekeeper || ''
        }, { headers });
        
        toast.success(`Task rescheduled to ${new Date(newDate).toLocaleDateString()}`);
        onReminderUpdate?.();
      }
      
      setRescheduleItem(null);
      setNewDate('');
      setShowDayPopup(false);
    } catch (err) {
      toast.error('Failed to reschedule');
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, item, itemType) => {
    e.stopPropagation();
    setDraggedItem({ ...item, itemType });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ ...item, itemType }));
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverDay(null);
  };

  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isPast(day)) {
      setDragOverDay(day);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the day cell entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDay(null);
    }
  };

  const handleDrop = async (e, targetDay) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDay(null);
    
    if (isPast(targetDay) || !draggedItem) {
      setDraggedItem(null);
      return;
    }
    
    // Build the target date string
    const targetDate = new Date(year, month, targetDay);
    const dateString = targetDate.toISOString().split('T')[0];
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (draggedItem.itemType === 'action') {
        await axios.put(`${API}/api/planned-actions/${draggedItem.id}`, {
          ...draggedItem,
          date: dateString
        }, { headers });
        
        toast.success(`Moved to ${targetDate.toLocaleDateString()}`);
        onActionUpdate?.();
      } else if (draggedItem.itemType === 'reminder') {
        // Create a planned action from the reminder with new date
        await axios.post(`${API}/api/hives/task/snooze-reminder`, {
          hiveId: draggedItem.hiveId,
          action: draggedItem.action,
          newDate: dateString,
          assignedBeekeeper: draggedItem.assignedBeekeeper || ''
        }, { headers });
        
        toast.success(`Rescheduled to ${targetDate.toLocaleDateString()}`);
        onReminderUpdate?.();
      }
    } catch (err) {
      toast.error('Failed to reschedule task');
    }
    
    setDraggedItem(null);
  };

  const getSelectedDayItems = () => {
    if (!selectedDay) return { reminders: [], actions: [], queenEvents: [] };
    return getItemsForDate(selectedDay);
  };

  const renderCalendarDays = () => {
    const days = [];
    
    for (let i = 0; i < startingDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-24 print:h-16 bg-stone-50/50 border border-stone-100"></div>
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const { reminders: dayReminders, actions: dayActions, queenEvents, treatmentEvents } = getItemsForDate(day);
      const totalItems = dayReminders.length + dayActions.length + queenEvents.length + treatmentEvents.length;
      const shownItems = [dayReminders, dayActions, queenEvents, treatmentEvents].filter(list => list.length > 0).length;
      const hiddenItems = totalItems - shownItems;
      const todayClass = isToday(day) ? 'ring-2 ring-amber-500 ring-inset' : '';
      const pastClass = isPast(day) ? 'bg-stone-50/80' : 'bg-white';
      const weather = getWeatherForDate(day);
      const badWeather = isBadWeather(day);
      const isDragOver = dragOverDay === day && !isPast(day);

      days.push(
        <div 
          key={day}
          onClick={() => handleDayClick(day)}
          onDragOver={(e) => handleDragOver(e, day)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, day)}
          className={`h-24 print:h-16 ${pastClass} ${todayClass} ${badWeather ? 'bg-blue-50/50' : ''} ${isDragOver ? 'bg-amber-100 ring-2 ring-amber-400' : ''} border border-stone-200 p-1 cursor-pointer hover:bg-amber-50 print:hover:bg-white transition-colors overflow-hidden relative`}
          data-testid={`calendar-day-${day}`}
        >
          {/* Weather Icon - top right corner */}
          {weather && (
            <div className="absolute top-0.5 right-0.5 print:top-0 print:right-0" title={`${weather.tempMax}°/${weather.tempMin}° ${weather.precipProb}% rain`}>
              <div className="flex items-center gap-0.5 text-[9px] print:text-[7px] text-stone-500">
                {getWeatherIcon(weather.code, 'sm')}
                <span className="font-medium">{weather.tempMax}°</span>
              </div>
            </div>
          )}

          {/* Day Number */}
          <div className={`text-sm print:text-xs font-semibold ${isToday(day) ? 'text-amber-600' : isPast(day) ? 'text-stone-400' : 'text-black'}`}>
            {day}
            {badWeather && !isPast(day) && (
              <span className="ml-1 text-[9px] text-blue-500">🌧️</span>
            )}
          </div>

          {/* Items Preview - Show action/task name with Hive ID and Yard when all yards selected */}
          <div className="mt-0.5 space-y-0.5 overflow-hidden">
            {queenEvents.slice(0, 1).map((event, idx) => (
              <div 
                key={`q-${idx}`}
                className="text-[9px] print:text-[7px] bg-pink-100 text-pink-800 px-1 py-0.5 rounded truncate flex items-center gap-0.5"
                title={`${event.label} - ${event.batchName}`}
              >
                <Crown className="h-2 w-2 flex-shrink-0" />
                <span className="truncate">{event.label}</span>
              </div>
            ))}
            {dayReminders.slice(0, 1).map((reminder, idx) => (
              <div 
                key={`r-${idx}`}
                draggable={!isPast(day)}
                onDragStart={(e) => handleDragStart(e, reminder, 'reminder')}
                onDragEnd={handleDragEnd}
                className={`text-[9px] print:text-[7px] bg-purple-100 text-purple-800 px-1 py-0.5 rounded truncate flex items-center gap-0.5 ${!isPast(day) ? 'cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-purple-400' : ''}`}
                title={`Hive ${reminder.hiveNumber}: ${reminder.action}${reminder.yard ? ` @ ${reminder.yard}` : ''}${!isPast(day) ? ' (drag to reschedule)' : ''}`}
              >
                <Clock className="h-2 w-2 flex-shrink-0" />
                <span className="truncate">
                  {selectedApiary === 'all' 
                    ? `H${reminder.hiveNumber} ${reminder.action?.slice(0, 8) || ''}${reminder.yard ? ` • ${reminder.yard.slice(0,6)}` : ''}`
                    : (reminder.action?.slice(0, 12) || `H${reminder.hiveNumber}`)}
                </span>
              </div>
            ))}
            {dayActions.slice(0, 1).map((action, idx) => (
              <div 
                key={`a-${idx}`}
                draggable={!isPast(day)}
                onDragStart={(e) => handleDragStart(e, action, 'action')}
                onDragEnd={handleDragEnd}
                className={`text-[9px] print:text-[7px] px-1 py-0.5 rounded truncate flex items-center gap-0.5 ${
                  action.status === 'completed' ? 'bg-green-100 text-green-700 line-through' :
                  action.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-amber-100 text-amber-800'
                } ${!isPast(day) && action.status !== 'completed' ? 'cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-amber-400' : ''}`}
                title={`${action.title || action.description}${action.hive_number ? ` - Hive #${action.hive_number}` : ''}${action.yard ? ` @ ${action.yard}` : ''}${action.status === 'completed' ? ' (done)' : !isPast(day) ? ' (drag to reschedule)' : ''}`}
              >
                <Check className="h-2 w-2 flex-shrink-0" />
                <span className="truncate">
                  {selectedApiary === 'all' 
                    ? `#${action.hive_number || '?'} ${(action.title || action.description)?.slice(0, 6) || ''}${action.yard ? ` • ${action.yard.slice(0,6)}` : ''}`
                    : ((action.title || action.description)?.slice(0, 12) || `#${action.hive_number || '?'}`)}
                </span>
              </div>
            ))}
            {treatmentEvents.slice(0, 1).map((tx, idx) => (
              <div
                key={`tx-${idx}`}
                className={`text-[9px] print:text-[7px] px-1 py-0.5 rounded truncate flex items-center gap-0.5 ${
                  tx.status === 'completed' ? 'bg-teal-100 text-teal-700 line-through' : 'bg-red-100 text-red-800'
                }`}
                title={`${tx.title || tx.planName} (${tx.status})`}
              >
                <ShieldAlert className="h-2 w-2 flex-shrink-0" />
                <span className="truncate">{(tx.title || tx.planName || '').slice(0, 14)}</span>
              </div>
            ))}
            {hiddenItems > 0 && (
              <div className="text-[10px] text-stone-600 font-semibold" data-testid={`calendar-day-more-${day}`}>+{hiddenItems} more</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const selectedDayData = getSelectedDayItems();
  const selectedDayWeather = selectedDay ? getWeatherForDate(selectedDay) : null;

  return (
    <>
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm print:shadow-none print:border-black" data-testid="monthly-calendar" id="printable-calendar">
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #printable-calendar, #printable-calendar * { visibility: visible; }
            #printable-calendar { position: absolute; left: 0; top: 0; width: 100%; border: 2px solid black !important; }
            .print\\:hidden { display: none !important; }
          }
        `}</style>

        {/* Header */}
        <div className="p-3 border-b border-stone-100 print:border-black">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-bold text-black text-base">📅 Monthly Calendar</h2>
            <div className="flex items-center gap-2 print:hidden flex-wrap">
              {/* Apiary Selector - filters tasks AND weather */}
              {yards.length > 0 && (
                <Select value={selectedApiary} onValueChange={handleApiaryChange}>
                  <SelectTrigger className="h-8 w-[180px] text-xs border-stone-200">
                    <MapPin className="h-3 w-3 mr-1 text-amber-500" />
                    <SelectValue placeholder="Filter by Apiary" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="flex items-center gap-2">
                        <span>🌍</span>
                        <span>All Apiaries</span>
                      </span>
                    </SelectItem>
                    {yards.map(yard => (
                      <SelectItem key={yard.id || yard.name} value={yard.id || yard.name}>
                        <span className="flex items-center gap-2">
                          <span>📍</span>
                          <span>{yard.name}</span>
                          {yard.suburb && <span className="text-stone-400 text-xs">({yard.suburb})</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={handlePrint} className="text-xs flex items-center gap-1">
                <Printer className="h-3 w-3" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} className="text-xs">Today</Button>
            </div>
          </div>
          {/* Show selected apiary filter info */}
          {selectedApiary !== 'all' && selectedApiaryName && (
            <div className="mt-2 text-xs text-amber-600 flex items-center gap-1 print:block">
              <MapPin className="h-3 w-3 print:hidden" />
              <span>Showing tasks for: <strong>{selectedApiaryName.split(' - ')[0]}</strong></span>
            </div>
          )}
        </div>

        {/* Month Navigation */}
        <div className="px-3 py-2 flex items-center justify-between border-b border-stone-100 bg-stone-50">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 hover:bg-stone-200 rounded-lg print:hidden">
            <ChevronLeft className="h-4 w-4 text-stone-600" />
          </button>
          <div className="text-base font-bold text-black">{monthNames[month]} {year}</div>
          <button onClick={() => navigateMonth(1)} className="p-1.5 hover:bg-stone-200 rounded-lg print:hidden">
            <ChevronRight className="h-4 w-4 text-stone-600" />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 border-b border-stone-200">
          {dayNames.map(day => (
            <div key={day} className="py-1.5 text-center text-xs font-semibold text-stone-500 bg-stone-50">{day}</div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">{renderCalendarDays()}</div>

        {/* Legend */}
        <div className="p-2 border-t border-stone-100 flex flex-wrap items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-pink-100 rounded border border-pink-300"></div>
            <span>Queen</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-purple-100 rounded border border-purple-300"></div>
            <span>Reminder</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-amber-100 rounded border border-amber-300"></div>
            <span>Action</span>
          </div>
          <div className="flex items-center gap-1">
            <span>🌧️</span>
            <span>Rain likely</span>
          </div>
        </div>
      </div>

      {/* Day Details Popup with Reschedule */}
      {showDayPopup && selectedDay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] print:hidden" onClick={() => { setShowDayPopup(false); setRescheduleItem(null); }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Popup Header with Weather */}
            <div className={`px-5 py-4 border-b ${selectedDayWeather && isBadWeather(selectedDay) ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg text-stone-800">
                    {dayNames[new Date(year, month, selectedDay).getDay()]}, {monthNames[month]} {selectedDay}
                  </h3>
                  {selectedDayWeather && (
                    <div className="flex items-center gap-2 mt-1 text-sm text-stone-600">
                      {getWeatherIcon(selectedDayWeather.code, 'md')}
                      <span>{selectedDayWeather.tempMax}° / {selectedDayWeather.tempMin}°</span>
                      {selectedDayWeather.precipProb > 0 && (
                        <span className="text-blue-600">💧 {selectedDayWeather.precipProb}%</span>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={() => { setShowDayPopup(false); setRescheduleItem(null); }} className="p-2 hover:bg-black/5 rounded-lg">
                  <X className="h-5 w-5 text-stone-500" />
                </button>
              </div>
              
              {/* Bad Weather Warning */}
              {selectedDayWeather && isBadWeather(selectedDay) && !isPast(selectedDay) && (
                <div className="mt-3 flex items-center gap-2 text-sm text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Rain expected - consider rescheduling tasks</span>
                </div>
              )}
            </div>

            {/* Popup Content */}
            <div className="p-5 overflow-y-auto max-h-[50vh]">
              {/* Reschedule Form */}
              {rescheduleItem && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Reschedule: {rescheduleItem.title || rescheduleItem.action}
                  </h4>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      className="flex-1"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <Button onClick={handleReschedule} size="sm" className="bg-amber-600 hover:bg-amber-700">
                      Save
                    </Button>
                    <Button onClick={() => setRescheduleItem(null)} variant="outline" size="sm">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Queen Events */}
              {selectedDayData.queenEvents.length > 0 && (
                <div className="mb-5">
                  <h4 className="font-semibold text-pink-800 mb-2 flex items-center gap-2">
                    <Crown className="h-4 w-4" />
                    Queen Rearing ({selectedDayData.queenEvents.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.queenEvents.map((event, idx) => (
                      <div key={idx} className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                        <div className="font-medium text-pink-900">{event.label}</div>
                        <div className="text-sm text-pink-700 mt-1">{event.batchName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reminders with Reschedule */}
              {selectedDayData.reminders.length > 0 && (
                <div className="mb-5">
                  <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Reminders ({selectedDayData.reminders.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.reminders.map((reminder, idx) => (
                      <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="font-medium text-purple-900">{reminder.action}</div>
                          {!isPast(selectedDay) && (
                            <button
                              onClick={() => setRescheduleItem({ ...reminder, itemType: 'reminder' })}
                              className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                            >
                              <CalendarDays className="h-3 w-3" />
                              Reschedule
                            </button>
                          )}
                        </div>
                        <div className="mt-2 space-y-1 text-sm">
                          <div className="flex items-center gap-2 text-purple-700">
                            <span className="font-semibold bg-purple-200 px-2 py-0.5 rounded">Hive {reminder.hiveNumber}</span>
                          </div>
                          {reminder.yard && (
                            <div className="flex items-center gap-2 text-purple-600">
                              <MapPin className="h-3.5 w-3.5" />
                              <span>{reminder.yard}</span>
                            </div>
                          )}
                          {reminder.assignedBeekeeper && (
                            <div className="flex items-center gap-2 text-purple-600">
                              <User className="h-3.5 w-3.5" />
                              <span>{reminder.assignedBeekeeper}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Planned Actions with Reschedule */}
              {selectedDayData.actions.length > 0 && (
                <div className="mb-5">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    Planned Actions ({selectedDayData.actions.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedDayData.actions.map((action, idx) => (
                      <div key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="font-medium text-amber-900">{action.title || action.description}</div>
                          {!isPast(selectedDay) && (
                            <button
                              onClick={() => setRescheduleItem({ ...action, itemType: 'action' })}
                              className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                            >
                              <CalendarDays className="h-3 w-3" />
                              Reschedule
                            </button>
                          )}
                        </div>
                        {action.description && action.title && (
                          <div className="text-sm text-amber-700 mt-1">{action.description}</div>
                        )}
                        <div className="mt-2 space-y-1 text-sm">
                          {action.hive_number && (
                            <div className="flex items-center gap-2 text-amber-700">
                              <span className="font-semibold bg-amber-200 px-2 py-0.5 rounded">Hive #{action.hive_number}</span>
                            </div>
                          )}
                          {action.yard && (
                            <div className="flex items-center gap-2 text-amber-600">
                              <MapPin className="h-3.5 w-3.5" />
                              <span>{action.yard}</span>
                            </div>
                          )}
                          {action.assignedBeekeeper && (
                            <div className="flex items-center gap-2 text-amber-600">
                              <User className="h-3.5 w-3.5" />
                              <span>{action.assignedBeekeeper}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Treatment Events */}
              {selectedDayData.treatmentEvents?.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="h-4 w-4 text-red-500" />
                    <span className="font-semibold text-sm text-red-700">
                      Treatments ({selectedDayData.treatmentEvents.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {selectedDayData.treatmentEvents.map((tx, idx) => (
                      <div key={`tx-${idx}`} className={`p-2 rounded-lg border ${
                        tx.status === 'completed' ? 'bg-teal-50 border-teal-200' : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className={`font-medium text-sm ${tx.status === 'completed' ? 'text-teal-800 line-through' : 'text-red-800'}`}>
                            {tx.title || tx.planName?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            tx.status === 'completed' ? 'bg-teal-100 text-teal-700' : tx.status === 'pending' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {tx.status}
                          </span>
                        </div>
                        {tx.description && <p className="text-xs text-stone-500 mt-1">{tx.description}</p>}
                        <p className="text-xs text-stone-400 mt-1">Plan: {tx.planName?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No items */}
              {selectedDayData.queenEvents.length === 0 && 
               selectedDayData.reminders.length === 0 && 
               selectedDayData.actions.length === 0 &&
               (selectedDayData.treatmentEvents?.length || 0) === 0 && (
                <div className="text-center py-8 text-stone-500">
                  <p>No scheduled items for this day</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 bg-stone-50 border-t border-stone-200">
              <Button onClick={() => { setShowDayPopup(false); setRescheduleItem(null); }} className="w-full" variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MonthlyCalendar;
